import React from "react";
import { getMediaModeLabel, isLocalMediaMode } from "../lib/streamUrls";

function MetricCard({ label, value, sub, tone = "default" }) {
  return (
    <div className={`ops-metric ops-metric--${tone}`}>
      <span className="ops-metric-label">{label}</span>
      <span className="ops-metric-value">{value}</span>
      {sub ? <span className="ops-metric-sub">{sub}</span> : null}
    </div>
  );
}

export default function OperationsPanel({
  devices,
  workerStatus,
  statusError,
  onReloadWorker,
  reloading,
}) {
  const summary = workerStatus?.summary || {};
  const sys = workerStatus?.system || {};
  const streamServer = workerStatus?.streamingServer || {};
  const totalApi = devices.length;
  const activeApi = devices.filter(
    (d) => d.isActive === true || d.IsActive === true
  ).length;
  const aiApi = devices.filter(
    (d) => d.isAiProcess === true || d.IsAiProcess === true
  ).length;

  const cpu =
    sys.cpuPercent != null ? `${Math.round(sys.cpuPercent)}%` : "—";
  const mem =
    sys.memoryPercent != null ? `${Math.round(sys.memoryPercent)}%` : "—";
  const memDetail =
    sys.memoryUsedGb != null
      ? `${sys.memoryUsedGb} / ${sys.memoryTotalGb} GB`
      : null;
  const activeStreams = streamServer.activeStreams || [];
  const unmappedStreams = streamServer.unmappedStreams || [];

  return (
    <section className="ops-panel">
      <div className="ops-panel-head">
        <div>
          <h3 className="ops-title">Enterprise operations</h3>
          <p className="ops-subtitle">
            RTSP camera pull → worker → RTMP platform ingest → WebRTC/HLS restream
          </p>
          <p className={`ops-mode-badge ${isLocalMediaMode() ? "ops-mode-badge--local" : "ops-mode-badge--prod"}`}>
            MediaMTX: {getMediaModeLabel()}
            {isLocalMediaMode() && " — API ingest/playback URLs ignored"}
          </p>
        </div>
        <button
          type="button"
          className="ops-reload-btn"
          onClick={onReloadWorker}
          disabled={reloading}
        >
          {reloading ? "Syncing…" : "Sync workers from API"}
        </button>
      </div>

      {statusError && (
        <p className="ops-alert">
          Worker status API unreachable ({statusError}). On the server: run{" "}
          <code>python worker.py</code> and proxy <code>/worker-status</code> to{" "}
          <code>127.0.0.1:8766</code> (do not expose port 8766 to the public internet).
          See <code>deploy/nginx-worker-status.conf.example</code>.
        </p>
      )}

      <div className="ops-metrics-grid">
        <MetricCard label="Devices (API)" value={totalApi} sub={`${activeApi} isActive`} />
        <MetricCard
          label="RTSP ingesting"
          value={summary.rtspIngesting ?? 0}
          sub="cameras pulling live video"
          tone="ingest"
        />
        <MetricCard
          label="RTMP publishing"
          value={summary.rtmpPublishing ?? 0}
          sub="restream into MediaMTX"
          tone="publish"
        />
        <MetricCard
          label="AI analytics"
          value={summary.aiAnalytics ?? 0}
          sub={`${aiApi} isAiProcess in API`}
          tone="ai"
        />
        <MetricCard label="CPU" value={cpu} sub={sys.cpuCores ? `${sys.cpuCores} cores` : null} tone="system" />
        <MetricCard label="RAM" value={mem} sub={memDetail} tone="system" />
        <MetricCard
          label="Active streams"
          value={streamServer.activeStreamsTotal ?? 0}
          sub={`${streamServer.linkedStreamsTotal ?? 0} linked`}
          tone="publish"
        />
        <MetricCard
          label="Unmapped streams"
          value={streamServer.unmappedStreamsTotal ?? 0}
          sub="active on MediaMTX, no device profile"
          tone="ai"
        />
      </div>

      {sys.gpuAvailable && (
        <p className="ops-gpu">
          GPU: {sys.gpuName} — {sys.gpuMemoryAllocatedGb} GB allocated
        </p>
      )}

      {!!streamServer.mediamtxError && (
        <p className="ops-alert">
          MediaMTX API read failed: {streamServer.mediamtxError}. Ensure MediaMTX control API
          is reachable from worker host ({streamServer.mediamtxApiBase}).
        </p>
      )}

      <div className="ops-streams-grid">
        <div className="ops-stream-box">
          <h4>Live streams on MediaMTX</h4>
          {activeStreams.length === 0 ? (
            <p className="ops-stream-muted">No active stream found.</p>
          ) : (
            <ul className="ops-stream-list">
                  {activeStreams.slice(0, 12).map((s) => (
                    <li key={s.streamId}>
                      <code>{s.streamId}</code>
                      <span className={s.hasPublisher ? "ops-stream-live" : "ops-stream-warn"}>
                        {s.hasPublisher ? `● ${s.sourceType || "publisher"}` : "○ no publisher"}
                      </span>
                      <span>{s.readers} viewer(s)</span>
                    </li>
                  ))}
            </ul>
          )}
        </div>

        <div className="ops-stream-box">
          <h4>Active unmapped streams</h4>
          {unmappedStreams.length === 0 ? (
            <p className="ops-stream-muted">All active streams are linked.</p>
          ) : (
            <ul className="ops-stream-list">
                  {unmappedStreams.slice(0, 12).map((s) => (
                    <li key={s.streamId}>
                      <code>{s.streamId}</code>
                      <span className={s.hasPublisher ? "ops-stream-live" : "ops-stream-warn"}>
                        {s.hasPublisher ? `● ${s.sourceType || "publisher"}` : "○ no publisher"}
                      </span>
                      <span>{s.readers} viewer(s)</span>
                      <span className="ops-stream-warn">unmapped</span>
                    </li>
                  ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
